/*
  GetCheckout (singleton)
  - Intercepts any attempt to go to Shopify checkout and redirects to a custom URL
  - Works with normal checkout buttons, forms, checkout links, and dynamic payment (Shopify payment) buttons
  - Uses event delegation (no MutationObserver) and stable handler bindings
  - Captures UTM params and appends them to redirect URL
  - Retrieves cart token from cookie or via /cart.js fallback
*/
(function () {
  'use strict';


  if (window.__GetCheckout) {
    return; // already initialized
  }


  const CONFIG = {
    // Change this to your desired in-store path
    // Example: '/process/checkout'
    redirectPath: '/process/checkout',
    // Enable to require a test flag in the URL before activating
    // When true, the script activates only if the URL contains `?checkout_test=true`
    test: false,
    testParam: 'checkout_test',
    // When in test mode and active, keep the test param on redirect URLs
    keepTestParam: true,
    // Optional: remove the test param from the current URL on page load and reload once.
    // WARNING: If enabled, the script will deactivate after the reload because the test flag is gone.
    stripTestParamFromUrlOnLoad: false,
    // Extendable selectors for dynamic checkout buttons (these will add to cart first)
    dynamicCheckoutSelectors: ['.shopify-payment-button__button', '.product-form__submit.button'],
    // Custom selectors for buttons that should redirect directly to checkout (no add to cart)
    customCheckoutSelectors: ['.easc-checkoutBtn', '.upcart-checkout-button'],
    log: false,
  };


  const logger = {
    log: (...args) => CONFIG.log && console.log('[GetCheckout]', ...args),
    warn: (...args) => CONFIG.log && console.warn('[GetCheckout]', ...args),
    error: (...args) => CONFIG.log && console.error('[GetCheckout]', ...args),
  };


  // --- helpers -------------------------------------------------------------
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }


  function pickUTMsFromCurrentLocation() {
    const allowed = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'ttclid', 'gclid']);
    const src = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    for (const [k, v] of src.entries()) {
      if (allowed.has(k) && v) out.set(k, v);
    }
    return out;
  }


  function getTestParamValue() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(CONFIG.testParam);
    } catch (_e) {
      return null;
    }
  }


  function shouldActivate() {
    if (!CONFIG.test) return true;
    return getTestParamValue() === 'true';
  }


  function getStoredTestFlag() {
    try { return localStorage.getItem(CONFIG.testParam) === 'true'; } catch (_e) { return false; }
  }


  function setStoredTestFlag(val) {
    try {
      if (val) localStorage.setItem(CONFIG.testParam, 'true');
      else localStorage.removeItem(CONFIG.testParam);
    } catch (_e) {}
  }


  function stripTestParamFromCurrentUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has(CONFIG.testParam)) {
        params.delete(CONFIG.testParam);
        const qs = params.toString();
        const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (_e) {}
  }


  function addTestParamToCurrentUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(CONFIG.testParam) !== 'true') {
        params.set(CONFIG.testParam, 'true');
        const qs = params.toString();
        const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch (_e) {}
  }


  // Sync URL and storage based on CONFIG.test and current param value
  function reconcileTestFlag() {
    const urlVal = getTestParamValue();
    if (!CONFIG.test) {
      // Clean everything when test mode is disabled
      setStoredTestFlag(false);
      if (urlVal !== null) stripTestParamFromCurrentUrl();
      return;
    }


    // CONFIG.test = true
    if (urlVal === 'true') {
      setStoredTestFlag(true);
      // ensure param stays in URL (already true)
      return;
    }
    if (urlVal === 'false') {
      // Explicitly turned off via URL -> clear and strip
      setStoredTestFlag(false);
      stripTestParamFromCurrentUrl();
      return;
    }
    // No URL param: if previously enabled in storage, keep it by adding to URL
    if (getStoredTestFlag()) {
      addTestParamToCurrentUrl();
    }
  }


  function saveUTMs(params) {
    try {
      localStorage.setItem('get-checkout-utm', params.toString());
    } catch (e) {
      logger.warn('Unable to store UTM params', e);
    }
  }


  function loadUTMs() {
    try {
      const str = localStorage.getItem('get-checkout-utm');
      return new URLSearchParams(str || '');
    } catch (e) {
      logger.warn('Unable to read UTM params', e);
      return new URLSearchParams();
    }
  }


  function ensureUTMsStored() {
    const current = pickUTMsFromCurrentLocation();
    if ([...current.keys()].length) {
      saveUTMs(current);
    }
  }


  async function getCartToken() {
    // Prefer cookie set by Shopify
    let token = getCookie('cart');
    if (token) return token;
    try {
      const res = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart.js', { credentials: 'same-origin' });
      if (res.ok) {
        const data = await res.json();
        if (data && data.token) return data.token;
      }
    } catch (e) {
      logger.warn('Failed to fetch cart token via /cart.js', e);
    }
    return '';
  }


  function buildRedirectURL(cartToken) {
    // Compose absolute URL for the redirect target
    const url = new URL(CONFIG.redirectPath, window.location.origin);
    const params = loadUTMs();
    if (cartToken) params.set('cart', cartToken);
    // Preserve test flag in redirects if test mode is on and active
    if (CONFIG.test && CONFIG.keepTestParam && getTestParamValue() === 'true') {
      params.set(CONFIG.testParam, 'true');
    }
    url.search = params.toString();
    return url.toString();
  }


  function isCheckoutPathname(pathname) {
    // Handles localized or prefixed paths (e.g., /en-eu/checkout)
    return /(^|\/)checkout(\b|\/)/.test(pathname);
  }


  function isCheckoutLink(a) {
    if (!a || !a.href) return false;
    const url = new URL(a.getAttribute('href'), window.location.origin);
    // Same-origin check; we only intercept in-store checkout attempts
    if (url.origin !== window.location.origin) return false;
    return isCheckoutPathname(url.pathname);
  }


  function isCheckoutForm(form) {
    if (!form) return false;
    const action = form.getAttribute('action') || '';
    // Interpret relative or absolute actions
    try {
      const url = new URL(action, window.location.origin);
      return isCheckoutPathname(url.pathname);
    } catch (_e) {
      // Non-URL action strings fall back to substring check
      return action.includes('/checkout');
    }
  }


  function isDynamicCheckoutButton(el) {
    if (!el) return false;
    try {
      for (const sel of CONFIG.dynamicCheckoutSelectors) {
        if (typeof sel === 'string' && el.closest && el.closest(sel)) return true;
      }
    } catch (_e) {}
    return false;
  }


  function isCustomCheckoutButton(el) {
    if (!el) return false;
    try {
      for (const sel of CONFIG.customCheckoutSelectors) {
        if (typeof sel === 'string' && el.closest && el.closest(sel)) return true;
      }
    } catch (_e) {}
    return false;
  }


  function findClosestProductForm(fromEl) {
    if (!fromEl || !(fromEl instanceof Element)) return null;
    return fromEl.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
  }


  async function addFormToCart(form) {
    if (!form) return false;
    try {
      const fd = new FormData(form);
      const endpoint = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js';
      const res = await fetch(endpoint, { method: 'POST', body: fd, credentials: 'same-origin' });
      if (!res.ok) {
        logger.warn('cart/add failed with status', res.status);
        return false;
      }
      await res.json().catch(() => ({}));
      return true;
    } catch (e) {
      logger.warn('cart/add error', e);
      return false;
    }
  }


  // --- core singleton ------------------------------------------------------
  class GetCheckout {
    constructor() {
      this.redirectLoading = false;


      // Bind handlers once so they can be added/removed reliably
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onAuxClickCapture = this.onAuxClickCapture.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.onKeydownCapture = this.onKeydownCapture.bind(this);


      ensureUTMsStored();


      // Persist/cleanup test flag and URL before activation decision
      reconcileTestFlag();


      // Determine if script should be active (supports test mode)
      this.active = shouldActivate();
      if (!this.active) {
        logger.log('inactive (test mode enabled but no test flag present)');
        return;
      }


      // Immediate guard: if we ever land on /checkout within theme context, redirect
      this.guardDirectCheckoutLoad();


      // Global delegated listeners (capture phase ensures we get the event first)
      document.addEventListener('click', this.onClickCapture, true);
      document.addEventListener('auxclick', this.onAuxClickCapture, true);
      document.addEventListener('submit', this.onSubmitCapture, true);
      document.addEventListener('keydown', this.onKeydownCapture, true);


      logger.log('initialized');
    }


    async toCheckout(afterAdd = false) {
      if (this.redirectLoading) return;
      this.redirectLoading = true;
      try {
        const token = await getCartToken();
        const target = buildRedirectURL(token);
        logger.log('redirecting to', target, afterAdd ? '(after add)' : '');
        window.location.href = target;
      } finally {
        // In case navigation is blocked by the browser for any reason
        setTimeout(() => {
          this.redirectLoading = false;
        }, 1000);
      }
    }


    guardDirectCheckoutLoad() {
      try {
        if (isCheckoutPathname(window.location.pathname)) {
          // Replace to avoid back button looping
          getCartToken().then((token) => {
            const target = buildRedirectURL(token);
            logger.log('guard redirect from /checkout to', target);
            window.location.replace(target);
          });
        }
      } catch (e) {
        logger.warn('guardDirectCheckoutLoad error', e);
      }
    }


    // Event handlers (delegated)
    async onClickCapture(e) {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;


      // Intercept checkout links
      const anchor = target.closest('a');
      if (anchor && isCheckoutLink(anchor)) {
        e.preventDefault();
        e.stopPropagation();
        return this.toCheckout();
      }


      // Intercept any explicit checkout button/input
      const btnOrInput = target.closest('button, input, a');


      if(target.shadowRoot) {
        const btnOrInput = target.shadowRoot.querySelector('a[href*="/checkout"]') || target.shadowRoot.querySelector('a[href*="/cart"]');
        btnOrInput.removeEventListener('click', this.onClickCapture, true);
        btnOrInput.addEventListener('click', this.onClickCapture, true);
        return;
      }
      
      if (btnOrInput) {
        // name="checkout" indicates standard checkout buttons
        if (btnOrInput.getAttribute('name') === 'checkout') {
          e.preventDefault();
          e.stopPropagation();
          return this.toCheckout();
        }


        // Custom checkout button (direct redirect, no add to cart)
        if (isCustomCheckoutButton(btnOrInput)) {
          e.preventDefault();
          e.stopPropagation();
          return this.toCheckout();
        }


        // Shopify Dynamic Checkout button
        if (isDynamicCheckoutButton(btnOrInput)) {
          e.preventDefault();
          e.stopPropagation();
          // Try adding closest product form then redirect
          const form = findClosestProductForm(btnOrInput);
          await addFormToCart(form);
          return this.toCheckout(true);
        }
      }


      // If a clickable element would submit a form to checkout, intercept
      const form = target.closest('form');
      if (form && isCheckoutForm(form)) {
        e.preventDefault();
        e.stopPropagation();
        return this.toCheckout();
      }
    }


    // Middle click on checkout link (new tab intent)
    onAuxClickCapture(e) {
      if (e.button !== 1) return; // middle mouse only
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const anchor = target.closest('a');
      if (anchor && isCheckoutLink(anchor)) {
        e.preventDefault();
        e.stopPropagation();
        this.toCheckout();
      }
    }


    onSubmitCapture(e) {
      const form = e.target instanceof HTMLFormElement ? e.target : null;
      if (form && isCheckoutForm(form)) {
        e.preventDefault();
        e.stopPropagation();
        this.toCheckout();
      }
    }


    onKeydownCapture(e) {
      if (e.key !== 'Enter') return;
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const form = target.closest('form');
      if (form && isCheckoutForm(form)) {
        e.preventDefault();
        e.stopPropagation();
        this.toCheckout();
      }
    }
  }


  // Initialize once
  window.__GetCheckout = new GetCheckout();
})();

